import { computed, ref, watch } from 'vue';
import { axiosClient } from '@/helpers/auth';
import { defineStore } from 'pinia';
import { useGlobalStore } from '@/store/storeGlobal';

import { useVerdilisteStore } from '@/store/storeVerdiliste';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useUserPreferencesStore } from '@/store/storeUserPreferences';
import
{
  isSystemColumnType,
  isSystemOrViewOnlyColumn,
  isTableColumnType,
  isIncludeInputFormNewDisabled,
  isReadOnlyDisabled,
  areAllVisible,
  areAllHidden
} from '@/utils/columnHelpers';
import { batchUpdateDatabaseObjectColumns } from '@/api/endpoints/databaseObjectColumns';
import { softDeleteRows as apiSoftDeleteRows, restoreRows as apiRestoreRows } from '@/api/endpoints/queryRows';
import { useDatabaseObjectStore } from '@/store/Setup/databaseObjectStore';
import { useTableDefinitionsStore } from '@/store/tableDefinitionsStore';

const debugPrefix = '[Table]';

// Pure helper-funksjoner (uten store-state)
function transformColumns (rawColumns, queryData)
{
  return Object.entries(rawColumns)
    .map(([key, col]) =>
    {
      const orderByInfo = queryData.orderBys?.find(
        (ob) => ob.columnName === key
      )

      const whereInfo = queryData.wheres?.find(
        (w) => w.columnName === key
      )

      const transformedSimpleList = col.simpleList?.map(item => ({
        value: item,
        label: item
      }))

      return {
        id: key,
        title: col.columnLabel,
        field: key,
        width: col.columnWidth,
        columnType: col.componentEdit,
        columnDefault: col.columnDefault,
        numberFormatDecimalPrecision: col.numberFormatDecimalPrecision,
        numberFormatShowThousandsSeparator: col.numberFormatShowThousandsSeparator,
        isReadOnly: col.isReadOnly,
        isRequired: col.isRequired,
        isIdentity: col.isIdentity,
        basicDataType: col.basicDataType,
        databaseObjectId: col.databaseObjectId,
        simpleList: transformedSimpleList,
        listOfValueObjectId: col.listOfValueObjectId,
        isSystemColumn: col.isSystemColumn,
        includeInputFormNew: col.includeInputFormNew,
        tableDefinitionColumn: col.tableDefinitionColumn,
        isNullable: col.isNullable,
        showInList: col.showInList,
        showInDetail: col.showInDetail,
        includeInExport: col.includeInExport,
        ordinal: col.ordinal,
        sortOrder: orderByInfo?.order || null,
        sortIndex: orderByInfo ? queryData.orderBys.indexOf(orderByInfo) : -1,
        filterOperator: whereInfo?.operator || null,
        filterValue: whereInfo?.value || null,
        isDirty: false,
        includeInputFormNewChanged: false,
        // Nye properties fra backend
        viewColumnType: col.viewColumnType,
        isReadOnlyEditable: col.isReadOnlyEditable,
        isUpdateable: col.isUpdateable,
        hasSqlFunction: col.hasSqlFunction,
        nullHandling: col.nullHandling
      }
    })
}
function prepareBatchColumnUpdate (column)
{
  const update = {
    columnName: column.field
  }
  const isTable = isTableColumnType(column)

  // Visningsinnstillinger - tillatt for alle kolonnetyper
  if (column.isDirty) {
    update.showInList = column.showInList
    update.showInDetail = column.showInDetail
    update.includeInExport = column.includeInExport
    update.columnWidth = column.width
    update.ordinal = column.ordinal
  }

  // isReadOnly - kun for TableColumn (og kun hvis ikke låst på tabell-nivå)
  if (column.isDirty && isTable && !isReadOnlyDisabled(column)) {
    update.isReadOnly = column.isReadOnly
  }

  // includeInputFormNew - kun for TableColumn
  if (column.includeInputFormNewChanged && isTable) {
    update.includeInputFormNew = column.includeInputFormNew
  }

  // componentEdit sendes ALDRI fra query-view (styres fra Setup)

  return update
}

function shouldSkipColumnForUpdate (column, field, includeSystem)
{
  if (!includeSystem && isSystemColumnType(column)) return true
  if (field === 'includeInputFormNew' && isSystemOrViewOnlyColumn(column)) return true
  if (field === 'includeInputFormNew' && isIncludeInputFormNewDisabled(column)) return true
  return false
}

function shouldSkipColumnForHide (column, field, includeSystem)
{
  if (!includeSystem && isSystemColumnType(column)) return true
  if (field === 'includeInputFormNew' && isSystemOrViewOnlyColumn(column)) return true
  if (field === 'includeInputFormNew' && column.nullHandling === 'required') return true
  return false
}

function markColumnAsChanged (column, field)
{
  if (field === 'includeInputFormNew') {
    column.includeInputFormNewChanged = true
  } else {
    column.isDirty = true
  }
}

export const useTableStore = defineStore('tableStore', () =>
{
  // GlobalStore
  const globalStore = useGlobalStore();
  const notificationsStore = useNotificationsStore();

  const verdilisteStore = useVerdilisteStore();
  const databaseObjectStore = useDatabaseObjectStore();
  const tableDefinitionsStore = useTableDefinitionsStore();


  const query = ref(null);
  const pagedResult = ref({});
  const showDetails = ref(false);
  const selectedItem = ref({});

  const queryUpdated = ref(false);
  const whereItems = ref([]);
  const orderByItems = ref([]);
  const totalRowsInTable = ref(null);

  const queryOperators = [
    { prefix: '=', operator: '=', icon: ['fal', 'equals'] },
    { prefix: '!=', operator: '!=', icon: ['fal', 'not-equal'] },
    { prefix: '!', operator: 'not like', icon: ['fal', 'search-minus'] },
    { prefix: '>=', operator: '>=', icon: ['fal', 'greater-than-equal'] },
    { prefix: '<=', operator: '<=', icon: ['fal', 'less-than-equal'] },
    { prefix: '<', operator: '<', icon: ['fal', 'less-than'] },
    { prefix: '>', operator: '>', icon: ['fal', 'greater-than'] },
    { prefix: '∈', operator: 'in', icon: false, symbol: '∈' },
    { prefix: '', operator: 'like', icon: ['fal', 'search'] },
  ];

  const selectedRows = ref([]);
  const queryRunning = ref(false);

  const columns = ref([]);

  const downloading = ref(false);
  const downloadingType = ref(null);

  const exportProgress = ref(null);
  const activeExportTaskId = ref(null); // Track export startet av DENNE fanen

  function getColumns ()
  {
    return Object.fromEntries(
      columns.value.map(col => [
        col.id,
        {
          ColumnName: col.field,
          ColumnWidth: col.width,
          Ordinal: col.ordinal,
          ShowInList: col.showInList,
          ShowInDetail: col.showInDetail,
          IncludeInputFormNew: col.includeInputFormNew,
          IncludeInExport: col.includeInExport,
        }
      ])
    );
  }

  function parseQueryOperator (inputText, columnName)
  {
    let found = false;
    const whereItem = {
      operator: 'like',
      icon: ['fal', 'search'],
      value: inputText,
      columnName,
    };
    for (const o of queryOperators) {
      if (inputText.startsWith(o.prefix) && !found) {
        found = true;
        whereItem.operator = o.operator;
        whereItem.icon = o.icon;
        whereItem.value = inputText.substring(o.prefix.length).trimStart();
      }
    }
    return whereItem;
  }

  function setSorting ({ columnName, order })
  {
    globalStore.debug(`${debugPrefix} setSorting:orderByItems`, orderByItems);
    globalStore.debug(`${debugPrefix} setSorting:columnName`, columnName);

    let itemFound = orderByItems.value.some(
      (o) => o.columnName === columnName && o.order === order,
    );

    globalStore.debug(`${debugPrefix} setSorting:itemFound`, itemFound);

    orderByItems.value = orderByItems.value.filter(
      (o) => o.columnName !== columnName,
    );

    if (!itemFound) {
      globalStore.debug(`${debugPrefix} setSorting:push`);
      orderByItems.value.push({
        columnName,
        order,
      });
    }

    globalStore.debug(`${debugPrefix} sorterAction`, orderByItems.value);
    queryUpdated.value = true;
    updateQuery();
  }

  function clearOrderBy ()
  {
    orderByItems.value = [];
    globalStore.debug(`${debugPrefix} clearOrderBy`, orderByItems.value);
    queryUpdated.value = true;
    updateQuery();
  }

  function addFilter (whereItem)
  {
    if (whereItem) {
      whereItems.value.push(whereItem);
    }
    queryUpdated.value = true;
    globalStore.debug(`${debugPrefix} addFilterAction`, whereItems.value, queryUpdated.value);
    updateQuery();
  }

  function addFilterActionOperator (columnName, operator, whereValue)
  {
    clearFilter(columnName);

    if (whereValue?.length > 0) {
      whereItems.value.push({ columnName, operator, value: JSON.stringify(whereValue) });
      queryUpdated.value = true;
    }
    globalStore.debug(`${debugPrefix} addFilterActionOperator`, whereItems.value, queryUpdated.value);
    updateQuery();
  }

  async function refreshQuery ()
  {
    globalStore.debug(`${debugPrefix} getItems()`);
    updateQuery();
    selectedItem.value = {};
    showDetails.value = false;
    queryUpdated.value = false;
    queryRunning.value = true;
    globalStore.debug(`${debugPrefix} axiosClient.post`, 'query');

    await getItems();

    queryRunning.value = false;
  }

  function getFilter (columnName)
  {
    return (
      whereItems.value.filter((item) => item.columnName === columnName) || []
    );
  }

  function hasFilter (columnName)
  {
    return getFilter(columnName).length;
  }

  function addFilterNull (columnName)
  {
    whereItems.value.push({ operator: 'IS NULL', columnName });
    queryUpdated.value = true;
    updateQuery();
  }

  function addFilterNotNull (columnName)
  {
    whereItems.value.push({ operator: 'IS NOT NULL', columnName });
    queryUpdated.value = true;
    updateQuery();
  }

  function clearFilter (columnName)
  {
    whereItems.value = whereItems.value.filter(
      (item) => item.columnName !== columnName,
    );
    queryUpdated.value = true;
  }

  function clearAllFilters ()
  {
    whereItems.value = [];
    queryUpdated.value = true;
  }

  function removeFilter (columnName, operator, value)
  {
    const idx = whereItems.value.findIndex((item) =>
      item.columnName === columnName &&
      item.operator === operator &&
      item.value === value,
    );
    whereItems.value.splice(idx, 1);
    queryUpdated.value = true;
  }

  function updateQuery ()
  {
    query.value.wheres = whereItems;
    query.value.orderBys = orderByItems;

    // Oppdater kun filterOperator og sortOrder på eksisterende kolonner (uten å re-transformere)
    columns.value = columns.value.map(col =>
    {
      const whereInfo = whereItems.value.find(w => w.columnName === col.field)
      const orderByInfo = orderByItems.value.find(ob => ob.columnName === col.field)

      return {
        ...col,
        filterOperator: whereInfo?.operator || null,
        filterValue: whereInfo?.value || null,
        sortOrder: orderByInfo?.order || null,
        sortIndex: orderByInfo ? orderByItems.value.indexOf(orderByInfo) : -1
      }
    })
  }

  async function getItems ()
  {
    const payload = {
      databaseObjectId: query.value.head.databaseObjectId,
      columns: getColumns(),
      wheres: whereItems.value,
      orderBys: orderByItems.value,
      page: pagedResult.value.page || 1,
      pageSize: 200,
      useOrderBy: true
    };
    await runQuery(payload);
  }

  async function runQuery (payload)
  {
    let res = null;
    try {
      res = await axiosClient.post('/v2/query/run', payload);
    } catch (e) {
      console.error('v2/query/run failed, falling back to v1:', e);
      res = await axiosClient.post('query', payload);
    }
    if (res) {
      const resultItems = res.data.items;

      // Oppdater selectedRows med nye data fra server, behold kun de som fortsatt eksisterer
      selectedRows.value = selectedRows.value
        .filter(selected => resultItems.some(item => item.icsRowId_ === selected.icsRowId_))
        .map(selected =>
        {
          // Finn matchende item fra nye resultater
          const updatedItem = resultItems.find(item => item.icsRowId_ === selected.icsRowId_);
          return { ...updatedItem, isSelected: true };
        });

      const items = resultItems.map(item => ({
        ...item,
        isSelected: selectedRows.value.some(selected => selected.icsRowId_ === item.icsRowId_)
      }));

      pagedResult.value = {
        ...res.data,
        items,
      };
    }
  }

  async function saveQuery (queryName, isGlobal)
  {
    const payload = {
      queryName,
      isGlobal,
      databaseObjectId: query.value.head.databaseObjectId,
      columns: getColumns(),
      wheres: whereItems.value,
      orderBys: orderByItems.value,
      page: pagedResult.value.page || 1,
      pageSize: 200,
      useOrderBy: true
    };
    await axiosClient.post('save-query', payload);
  }

  async function setResult (data)
  {
    const queryRequest = data.query;
    const fetchPromises = queryRequest.valueLists.map(value => verdilisteStore.fetchVerdiListe(value));
    await Promise.all(fetchPromises);

    // Tøm gamle data
    totalRowsInTable.value = null;
    whereItems.value = [];
    orderByItems.value = [];
    pagedResult.value = {};
    selectedRows.value = []; // Reset selection when setting new result

    // Sett nye data
    totalRowsInTable.value = data.total;
    whereItems.value = data.query.wheres.map(item => ({
      ...item,
      icon: item.icon || queryOperators.find(op => op.operator === item.operator)?.icon,
    }));
    orderByItems.value = data.query.orderBys;
    query.value = data.query;
    columns.value = transformColumns(data.columns, data.query);

    // Preprosesser items med isSelected property
    const items = data.items.map(item => ({
      ...item,
      isSelected: false // All items start unselected when loading new data
    }));
    pagedResult.value = {
      ...data,
      items
    };
  }

  async function getQuery (databaseObjectId, externalRowId)
  {
    notificationsStore.clearEvents();
    selectedItem.value = {};
    showDetails.value = false;
    queryUpdated.value = false;
    selectedRows.value = [];

    const params = { databaseObjectId, externalRowId };
    globalStore.debug(`${debugPrefix} getQuery: `, params);
    queryRunning.value = true;
    const res = await axiosClient.get('query', { params });
    globalStore.debug(`${debugPrefix} axiosClient.get query result: `, res);
    if (res) {
      await setResult(res.data);
      if (externalRowId && pagedResult.value.items.length > 0) {
        globalStore.debug(`${debugPrefix} row: `, externalRowId, pagedResult.value.items[0]);
        const firstItem = pagedResult.value.items[0];
        firstItem.isSelected = true; // Set isSelected flag directly on the item
        selectedItem.value = firstItem;
        selectedRows.value = [firstItem]; // Update selectedRows to match
        showDetails.value = true;
      }
    }
    queryRunning.value = false;
  }

  function hasOrderByAsc (columnName)
  {
    let item = orderByItems.value.find((item) => item.columnName === columnName);

    return item?.order === 'ASC';

  }

  function hasOrderByDesc (columnName)
  {
    let item = orderByItems.value.find((item) => item.columnName === columnName);

    return item?.order === 'DESC';
  }

  /**
   * Hjelpefunksjon for å sammenligne rowIds på tvers av number/string typer.
   * Bigint i SQL Server (2^63-1) serialiseres som string i JSON når verdien > 2^53-1.
   */
  function rowIdMatches (rowIds, targetId)
  {
    // Konverter alt til strings for konsistent sammenligning
    const targetStr = String(targetId);
    return rowIds.some(id => String(id) === targetStr);
  }

  async function saveData ({ databaseObjectId, rowId, columnName, oldValue, newValue })
  {
    if (oldValue === newValue) return; // no change...

    // Valider at rowId er gyldig (må være positiv for å kunne redigeres)
    if (!rowId || rowId <= 0) {
      console.warn(`${debugPrefix} saveData: Invalid rowId (${rowId}), skipping save operation - rad er skrivebeskyttet`);
      return;
    }

    const rowIds = [rowId];
    globalStore.debug(`${debugPrefix} axiosClient.post`, 'UpdateRecord');
    const payload = {
      databaseObjectId,
      rowIds,
      columnName,
      oldValue,
      newValue,
    };
    await updateRecords(payload);
  }

  async function updateRecords (payload)
  {
    const response = await axiosClient.patch('update-record', payload);
    if (response?.data) {
      const updatedRows = response.data.updatedRows;

      // Lokal synkronisering: Oppdater pagedResult.items basert på payload
      if (pagedResult.value?.items && payload.rowIds && payload.columnName) {
        pagedResult.value.items = pagedResult.value.items.map(item =>
        {
          // Sjekk om denne raden ble oppdatert (bruk string-sammenligning for bigint-støtte)
          if (rowIdMatches(payload.rowIds, item.icsRowId_)) {
            // Opprett nytt objekt for reaktivitet
            return {
              ...item,
              [payload.columnName]: payload.newValue
            };
          }
          return item;
        });

        // Synkroniser selectedRows med oppdaterte verdier fra pagedResult
        selectedRows.value = selectedRows.value.map(selected =>
        {
          if (rowIdMatches(payload.rowIds, selected.icsRowId_)) {
            const updatedItem = pagedResult.value.items.find(
              item => String(item.icsRowId_) === String(selected.icsRowId_)
            );
            return updatedItem ? { ...updatedItem, isSelected: true } : selected;
          }
          return selected;
        });

        globalStore.debug(`${debugPrefix} Lokalt oppdatert ${payload.rowIds.length} rader i kolonne ${payload.columnName}`);
      }

      notificationsStore.addSuccess('Lagret', `Kolonne ${payload.columnName} ble endret på ${updatedRows} rader`);
    }
  }

  /**
   * Soft-delete rader via dedikert endepunkt (omgår systemkolonne-beskyttelse)
   */
  async function softDeleteRows ({ databaseObjectId, rowIds })
  {
    try {
      const response = await apiSoftDeleteRows({ databaseObjectId, rowIds });

      // Lokal synkronisering: Oppdater ICS_SoftDeleted i pagedResult.items
      if (pagedResult.value?.items) {
        pagedResult.value.items = pagedResult.value.items.map(item =>
        {
          if (rowIdMatches(rowIds, item.icsRowId_)) {
            return { ...item, ICS_SoftDeleted: true };
          }
          return item;
        });

        // Synkroniser selectedRows med oppdaterte verdier fra pagedResult
        // (viktig for at alle felt inkludert timestamps holdes synkronisert)
        selectedRows.value = selectedRows.value.map(selected =>
        {
          if (rowIdMatches(rowIds, selected.icsRowId_)) {
            const updatedItem = pagedResult.value.items.find(
              item => String(item.icsRowId_) === String(selected.icsRowId_)
            );
            return updatedItem ? { ...updatedItem, isSelected: true } : { ...selected, ICS_SoftDeleted: true, isSelected: true };
          }
          return selected;
        });
      } else {
        // Fallback hvis pagedResult ikke finnes - oppdater kun ICS_SoftDeleted
        selectedRows.value = selectedRows.value.map(selected =>
        {
          if (rowIdMatches(rowIds, selected.icsRowId_)) {
            return { ...selected, ICS_SoftDeleted: true, isSelected: true };
          }
          return selected;
        });
      }

      notificationsStore.addSuccess('Slettet', `${response.deletedRows} rad(er) ble slettet`);
      return response;
    } catch (error) {
      console.error(`${debugPrefix} softDeleteRows failed:`, error);
      notificationsStore.addError('Sletting feilet', error.response?.data?.detail || error.message || 'Kunne ikke slette rader');
      throw error;
    }
  }

  /**
   * Gjenopprett rader via dedikert endepunkt (omgår systemkolonne-beskyttelse)
   */
  async function restoreRows ({ databaseObjectId, rowIds })
  {
    try {
      const response = await apiRestoreRows({ databaseObjectId, rowIds });

      // Lokal synkronisering: Oppdater ICS_SoftDeleted i pagedResult.items
      if (pagedResult.value?.items) {
        pagedResult.value.items = pagedResult.value.items.map(item =>
        {
          if (rowIdMatches(rowIds, item.icsRowId_)) {
            return { ...item, ICS_SoftDeleted: false };
          }
          return item;
        });

        // Synkroniser selectedRows med oppdaterte verdier fra pagedResult
        // (viktig for at alle felt inkludert timestamps holdes synkronisert)
        selectedRows.value = selectedRows.value.map(selected =>
        {
          if (rowIdMatches(rowIds, selected.icsRowId_)) {
            const updatedItem = pagedResult.value.items.find(
              item => String(item.icsRowId_) === String(selected.icsRowId_)
            );
            return updatedItem ? { ...updatedItem, isSelected: true } : { ...selected, ICS_SoftDeleted: false, isSelected: true };
          }
          return selected;
        });
      } else {
        // Fallback hvis pagedResult ikke finnes - oppdater kun ICS_SoftDeleted
        selectedRows.value = selectedRows.value.map(selected =>
        {
          if (rowIdMatches(rowIds, selected.icsRowId_)) {
            return { ...selected, ICS_SoftDeleted: false, isSelected: true };
          }
          return selected;
        });
      }

      notificationsStore.addSuccess('Gjenopprettet', `${response.restoredRows} rad(er) ble gjenopprettet`);
      return response;
    } catch (error) {
      console.error(`${debugPrefix} restoreRows failed:`, error);
      notificationsStore.addError('Gjenoppretting feilet', error.response?.data?.detail || error.message || 'Kunne ikke gjenopprette rader');
      throw error;
    }
  }

  async function addRecord ({ databaseObjectId, inputValues })
  {
    const inputData = {
      databaseObjectId,
      inputValues,
    };
    globalStore.debug(`${debugPrefix} axiosClient.post`, 'AddRecord', inputData);

    try {
      const response = await axiosClient.post('add-record', inputData);

      if (response) {
        await getItems();
        notificationsStore.addSuccess('Rad opprettet', 'Rad ble opprettet');
        return { success: true };
      }

      return { success: false };
    } catch (error) {
      // Hvis det er en valideringsfeil fra backend
      if (error?.response?.status === 400 && error?.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors };
      }

      // For andre feil, la notificationsStore håndtere det
      throw error;
    }
  }

  function download (fileType)
  {
    if (downloading.value) {
      notificationsStore.addWarning('Nedlasting pågår', 'Vent til pågående nedlasting er ferdig før du starter en ny.');
      return;
    }

    globalStore.debug(`${debugPrefix} databaseObjectId`, databaseObjectId);

    const fileName = query.value.head.systemObjectName +
      '_' +
      new Date()
        .toISOString()
        .replaceAll(':', '')
        .substring(0, 10);

    updateQuery();
    query.value.exportFormat = fileType;
    if (fileType === 'Excel') {
      query.value.exportFileName = fileName + '.xlsx';
    } else {
      query.value.exportFileName = fileName + '.zip';
    }
    downloadExcelWithAxios(query.value.exportFileName);
  }

  function forceFileDownload (data, fileName, contentType = null)
  {
    try {
      const blob = contentType ? new Blob([data], { type: contentType }) : new Blob([data]);
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error in forceFileDownload:', error);
      notificationsStore.addError('Nedlastingsfeil', 'Det oppstod en feil under nedlasting av filen.');
    }
  }

  function downloadExcelWithAxios (filename)
  {
    if (downloading.value) {
      return; // Ekstra sikkerhet mot parallelle nedlastinger
    }

    downloading.value = true;
    downloadingType.value = query.value.exportFormat;

    axiosClient({
      method: 'post',
      url: 'download',
      data: query.value,
      responseType: 'arraybuffer',
    })
      .then(({ data }) =>
      {
        globalStore.debug(`${debugPrefix} filename: `, filename);
        forceFileDownload(data, filename);
      })
      .catch(async ({ response }) =>
      {
        globalStore.debug(`${debugPrefix} error occurred: `, response, response.data);
        try {
          const error = JSON.parse(await (new Blob([response.data])).text());
          notificationsStore.addError('Nedlasting feilet', error.title);
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          notificationsStore.addError('Nedlasting feilet', 'Det oppstod en uventet feil under nedlasting.');
        }
      })
      .finally(() =>
      {
        downloading.value = false;
        downloadingType.value = null;
      });
  }

  const auditLog = ref([]);

  async function getAuditLog (databaseObjectId, rowId)
  {
    const response = await axiosClient.get('audit', { params: { databaseObjectId, rowId } });
    if (response?.data) {
      auditLog.value = response.data;
    }
  }

  const isSelected = (row) =>
  {
    return selectedRows.value.some(item => item.icsRowId_ === row.icsRowId_);
  };

  const hasSelectedRows = computed(() => selectedRows.value.length > 0);

  function selectSingleRow (row)
  {
    selectedRows.value = [row];
  }

  function addRowSelection (row)
  {
    if (!isSelected(row)) {
      selectedRows.value = [...selectedRows.value, row];
    }
  }

  function addRangeSelection (row)
  {
    if (!pagedResult.value?.items) return;

    const lastSelectedRow = selectedRows.value[selectedRows.value.length - 1];
    if (lastSelectedRow) {
      const startIndex = pagedResult.value.items.findIndex(item => item.icsRowId_ === lastSelectedRow.icsRowId_);
      const endIndex = pagedResult.value.items.findIndex(item => item.icsRowId_ === row.icsRowId_);
      const range = pagedResult.value.items.slice(
        Math.min(startIndex, endIndex),
        Math.max(startIndex, endIndex) + 1
      );
      selectedRows.value = [...new Set([...selectedRows.value, ...range])];
    } else {
      addRowSelection(row);
    }
  }

  function toggleRowSelection (row)
  {
    const index = selectedRows.value.findIndex(item => item.icsRowId_ === row.icsRowId_);
    if (index === -1) {
      selectedRows.value.push(row);
      row.isSelected = true;
    } else {
      selectedRows.value.splice(index, 1);
      row.isSelected = false;
    }
  }

  function clearSelection ()
  {
    selectedRows.value = [];
  }

  watch(selectedRows, (newSelectedRows) =>
  {
    if (pagedResult.value?.items) {
      for (const item of pagedResult.value.items) {
        item.isSelected = newSelectedRows.some(selected => selected.icsRowId_ === item.icsRowId_);
      }
    }
  });

  function handleSelectAll ()
  {
    if (pagedResult.value?.items?.length > 0) {
      // Set isSelected=true for all items
      for (const item of pagedResult.value.items) {
        item.isSelected = true;
      }
      // Update selectedRows with all items
      selectedRows.value = [...pagedResult.value.items];
      console.log("Selected all items:", selectedRows.value);
    }
  }

  function handleSelectInverted ()
  {
    if (pagedResult.value?.items) {
      // Invert isSelected for all items
      for (const item of pagedResult.value.items) {
        item.isSelected = !item.isSelected;
      }
      // Update selectedRows with newly selected items
      selectedRows.value = pagedResult.value.items.filter(item => item.isSelected);
      console.log("Inverted selected items:", selectedRows.value);
    }
  }

  function unselectItems ()
  {
    // Clear isSelected from all items
    if (pagedResult.value?.items) {
      for (const item of pagedResult.value.items) {
        item.isSelected = false;
      }
    }
    // Clear selectedRows
    selectedRows.value = [];
  }

  function handleBulkSelection (selection)
  {
    console.log(`Store received: ${selection}`);
    switch (selection) {
      case 'inverted':
        handleSelectInverted();
        break;
      case 'all':
        handleSelectAll();
        break;
      case 'unselect':
        unselectItems();
        break;
      default:
        console.warn(`Unknown selection: ${selection}`);
    }
  }

  const totalItems = computed(() => pagedResult.value?.items?.length || 0)
  const selectedCount = computed(() => pagedResult.value?.items?.filter(item => item.isSelected).length || 0)

  const getSelectionIcon = computed(() =>
  {
    if (selectedCount.value === 0) return ['fal', 'square']
    if (selectedCount.value === totalItems.value) return ['fas', 'square-check']
    return ['fas', 'square-minus']
  })

  const selectedRowIds = computed(() => selectedRows.value.map(row => row.icsRowId_).join('-'))

  function updateColumnWidth (columnField, newWidth)
  {
    const column = columns.value.find(col => col.field === columnField)
    if (column) {
      column.width = newWidth
      column.isDirty = true
      globalStore.debug(`${debugPrefix} Column width updated and marked as dirty:`, columnField)
    }
  }

  function updateColumnOrdinal (columnField, newOrdinal)
  {
    const column = columns.value.find(col => col.field === columnField)
    if (column) {
      column.ordinal = newOrdinal
      column.isDirty = true
      globalStore.debug(`${debugPrefix} Column ordinal updated and marked as dirty:`, columnField)
    }
  }

  function updateColumnVisibility (columnField, isVisible)
  {
    const column = columns.value.find(col => col.field === columnField)
    if (column) {
      column.showInList = isVisible
      column.isDirty = true
      globalStore.debug(`${debugPrefix} Column visibility updated and marked as dirty:`, columnField)
    }
  }

  function updateColumnInDetails (columnField, showInDetail)
  {
    const column = columns.value.find(col => col.field === columnField)
    if (column) {
      column.showInDetail = showInDetail
      column.isDirty = true
      globalStore.debug(`${debugPrefix} Column showInDetail updated and marked as dirty:`, columnField)
    }
  }

  function updateColumnIncludeInExport (columnField, includeInExport)
  {
    const column = columns.value.find(col => col.field === columnField)
    if (column) {
      column.includeInExport = includeInExport
      column.isDirty = true
      globalStore.debug(`${debugPrefix} Column visibility updated and marked as dirty:`, columnField)
    }
  }

  function updateColumnInputFormNew (columnField, includeInputFormNew)
  {
    const column = columns.value.find(col => col.field === columnField)
    if (!column) return

    // includeInputFormNew er kun relevant for TableColumn
    if (!isTableColumnType(column)) {
      globalStore.debug(`${debugPrefix} Ignoring includeInputFormNew for non-TableColumn:`, columnField)
      return
    }

    column.includeInputFormNew = includeInputFormNew
    column.includeInputFormNewChanged = true
    globalStore.debug(`${debugPrefix} Column includeInputFormNew updated and marked as changed:`, columnField)
  }

  // Mass update-operasjoner
  function showAllColumns (field = 'showInList', includeSystem = false)
  {
    if (areAllVisible(columns.value, field, includeSystem)) {
      return
    }

    for (const column of columns.value) {
      if (shouldSkipColumnForUpdate(column, field, includeSystem)) continue
      if (column[field] === true) continue

      column[field] = true
      markColumnAsChanged(column, field)
    }
  }

  function hideAllColumns (field = 'showInList', includeSystem = false)
  {
    if (areAllHidden(columns.value, field, includeSystem)) {
      return
    }

    for (const column of columns.value) {
      if (shouldSkipColumnForHide(column, field, includeSystem)) continue
      if (column[field] === false) continue

      column[field] = false
      markColumnAsChanged(column, field)
    }
  }

  function showAllSystemColumns (field = 'showInList')
  {
    for (const column of columns.value) {
      if (!isSystemColumnType(column)) continue
      if (field === 'includeInputFormNew') continue // SystemColumn kan ikke ha includeInputFormNew

      if (column[field] === true) continue

      column[field] = true
      column.isDirty = true
    }
  }

  function hideAllSystemColumns (field = 'showInList')
  {
    for (const column of columns.value) {
      if (!isSystemColumnType(column)) continue
      if (field === 'includeInputFormNew') continue // SystemColumn kan ikke ha includeInputFormNew

      if (column[field] === false) continue

      column[field] = false
      column.isDirty = true
    }
  }

  function setAllReadOnly (value)
  {
    for (const column of columns.value) {
      // Skip SystemColumn og ViewOnlyColumn (de skal alltid være read-only)
      if (isSystemOrViewOnlyColumn(column)) continue

      // Skip kolonner som ikke kan endres (disabled)
      if (isReadOnlyDisabled(column)) continue

      // Skip hvis allerede i ønsket tilstand
      if (column.isReadOnly === value) continue

      column.isReadOnly = value
      column.isDirty = true
    }
  }

  function updateTableDefinitionsCache (databaseObject)
  {
    if (!databaseObject?.tableDefinitionId) return

    const table = tableDefinitionsStore.getTableByDefinitionId(databaseObject.tableDefinitionId)
    if (!table?.columns) return

    for (const column of columns.value) {
      const col = table.columns.find(c => c.dbColumnName === column.field)
      if (col) {
        col.includeInputFormNew = column.includeInputFormNew
        col.isReadOnly = column.isReadOnly
        col.showInList = column.showInList
        col.showInDetail = column.showInDetail
        col.includeInExport = column.includeInExport
        col.columnWidth = column.width
      }
    }
  }

  function showSuccessMessage (dirtyColumns, isBatch)
  {
    if (isBatch) {
      if (dirtyColumns.length < 10) {
        const columnNames = dirtyColumns.map(col => col.title || col.field).join(', ')
        notificationsStore.addSuccess(`Lagret endringer for kolonnene: ${columnNames}`)
      } else {
        notificationsStore.addSuccess(`Lagret endringer for ${dirtyColumns.length} kolonner`)
      }
    } else {
      notificationsStore.addSuccess(`Kolonne ${dirtyColumns[0].field} er lagret`)
    }
  }

  async function saveColumnChanges (databaseObjectId)
  {
    const dirtyColumns = columns.value.filter(column => column.isDirty || column.includeInputFormNewChanged)

    if (dirtyColumns.length === 0) {
      notificationsStore.addInfo('Ingen endringer å lagre')
      return
    }

    const isBatch = dirtyColumns.length > 1

    try {
      let databaseObject = databaseObjectStore.wsItem
      if (!databaseObject?.tableDefinitionId && databaseObjectId) {
        await databaseObjectStore.fetchDatabaseObjectById(databaseObjectId)
        databaseObject = databaseObjectStore.wsItem
      }

      const batchUpdates = dirtyColumns.map(col => prepareBatchColumnUpdate(col))
      await batchUpdateDatabaseObjectColumns(databaseObjectId, batchUpdates)

      for (const column of dirtyColumns) {
        column.isDirty = false
        column.includeInputFormNewChanged = false
      }

      await refreshQuery()

      columns.value.sort((a, b) =>
      {
        const ordinalA = a.ordinal ?? 0
        const ordinalB = b.ordinal ?? 0
        return ordinalA - ordinalB
      })

      updateTableDefinitionsCache(databaseObject)
      showSuccessMessage(dirtyColumns, isBatch)
    } catch (error) {
      notificationsStore.addError('Feil ved lagring av kolonneinnstillinger', error.message)
      throw error
    }
  }

  async function loadQuery (payload)
  {
    // Setter query.value.head
    if (!query.value) query.value = { head: {} };
    query.value.head.databaseObjectId = payload.databaseObjectId;

    columns.value = columns.value.map(existingCol =>
    {
      const payloadCol = payload.columns[existingCol.id];

      if (payloadCol) {
        // Merk: lowercase property names!
        return {
          ...existingCol,
          width: payloadCol.columnWidth,
          ordinal: payloadCol.ordinal,
          showInList: payloadCol.showInList,
          showInDetail: payloadCol.showInDetail,
          includeInputFormNew: payloadCol.includeInputFormNew,
          tableDefinitionColumn: payloadCol.tableDefinitionColumn,
          includeInExport: payloadCol.includeInExport,
          includeInputFormNewChanged: false // Reset flag når data lastes
        };
      }
      return existingCol;
    });

    whereItems.value = payload.wheres;
    orderByItems.value = payload.orderBys;

    // Setter paging
    if (!pagedResult.value) pagedResult.value = {};
    pagedResult.value.page = payload.page;

    // Oppdaterer data
    await runQuery(payload);
  }

  async function deleteQuery (queryName, isGlobal, databaseObjectId)
  {
    await axiosClient.delete('delete-query', {
      params: {
        isGlobal,
        databaseObjectId,
        queryName
      }
    });
    notificationsStore.addSuccess('Spørring slettet', `Spørringen "${queryName}" ble slettet`);
  }

  async function downloadExport (settings)
  {
    try {
      downloading.value = true
      downloadingType.value = 'Excel'

      const response = await axiosClient.post(
        '/v2/export',
        {
          databaseObjectId: query.value.head.databaseObjectId,
          wheres: query.value.wheres || [],
          orderBys: query.value.orderBys || [],
          columns: getColumns() || {},
          includeHeaders: settings.includeHeaders,
          exportType: settings.exportType,
          sheetName: settings.sheetName || query.value?.head?.systemObjectName || 'Sheet1',
          dateFormat: settings.dateFormat,
          format: settings.format,
          encoding: settings.encoding,
          delimiter: settings.delimiter,
          compressed: settings.compress,
          includeData: settings.includeData ?? true
        },
        {
          responseType: 'json',
          maxRedirects: 0,
          validateStatus: status => status < 500
        }
      )

      console.log('Export response status:', response.status)
      console.log('Export response data:', response.data)

      if (response.status === 202 && response.data?.taskId) {
        console.log('Asynkron eksport startet, taskId:', response.data.taskId)
        activeExportTaskId.value = response.data.taskId; // Track denne fanens eksport
      } else if (response.status >= 400) {
        notificationsStore.addError('Eksport feilet', response.data?.title || 'Ukjent feil')
      }
    } catch (error) {
      console.error('Error downloading Excel:', error)
      notificationsStore.addError('Eksport feilet', 'Kunne ikke eksportere til Excel')
    }
  }

  // Helper: Hent og konverter datoformat fra user preferences
  const getDateFormatFromPreferences = async () => {
    const userPreferencesStore = useUserPreferencesStore()
    if (!userPreferencesStore.userPreferences) {
      await userPreferencesStore.getUserPreferences()
    }
    const dateTimeFormat = userPreferencesStore.userPreferences?.dateTimeFormat || 'DMY'
    switch (dateTimeFormat.toUpperCase()) {
      case 'MDY':
        return 'MM.dd.yyyy'
      case 'YMD':
        return 'yyyy.MM.dd'
      case 'DMY':
      default:
        return 'dd.MM.yyyy'
    }
  };

  // Helper: Bygg kolonnemapping for import
  const buildColumnMappings = (exportColumns, allColumns, dateFormat) => {
    const columnMappings = []
    let ordinal = 1

    // Sjekk om IcsExternalId_ allerede er inkludert
    const hasIcsExternalId = exportColumns.some(col => col.field === 'IcsExternalId_')

    // Legg til IcsExternalId_ først hvis den ikke allerede er inkludert
    if (!hasIcsExternalId) {
      const icsExternalIdColumn = allColumns.find(col => col.field === 'IcsExternalId_')
      if (icsExternalIdColumn) {
        columnMappings.push({
          ordinal: ordinal++,
          sourceColumnName: icsExternalIdColumn.title || 'IcsExternalId_',
          targetColumnName: 'IcsExternalId_',
          isKey: true,
          isCommentColumn: false,
          dateFormat: '',
          nullValue: ''
        })
      }
    }

    // Legg til alle eksport-kolonner
    for (const col of exportColumns) {
      const isCommentColumn = col.columnType === 'editComment' || false
      const isKey = col.field === 'IcsExternalId_'
      const colDateFormat = (col.columnType === 'editDate' || col.columnType === 'editDateTime') ? dateFormat : ''
      const sourceColumnName = (col.title && col.title !== col.field) ? col.title : col.field

      columnMappings.push({
        ordinal: ordinal++,
        sourceColumnName,
        targetColumnName: col.field,
        isKey,
        isCommentColumn,
        dateFormat: colDateFormat,
        nullValue: ''
      })
    }

    return columnMappings
  };

  // Helper: Bygg filinnstillinger for import
  const buildFileReaderOptions = exportSettings => {
    const separator = exportSettings.delimiter || (exportSettings.format === 'TSV' ? '\t' : ',')
    const finalSeparator = exportSettings.exportType === 'XLSX' ? null : separator
    const quote = exportSettings.exportType === 'XLSX' ? null : '"'
    const escape = exportSettings.exportType === 'XLSX' ? null : '"'
    const encoding = exportSettings.encoding || 'UTF-8'
    const headerRow = exportSettings.includeHeaders ? 1 : 0
    const firstDataRow = exportSettings.includeHeaders ? 2 : 1

    return {
      separator: finalSeparator,
      quote,
      escape,
      encoding,
      headerRow,
      firstDataRow
    }
  };

  async function createImportTemplateFromExport (exportSettings)
  {
    try {
      if (!query.value?.head) {
        notificationsStore.addError('Mangler tabellinformasjon', 'Kunne ikke opprette importmal')
        return
      }

      // Hent datoformat
      const dateFormat = await getDateFormatFromPreferences()

      // Hent alle kolonner med IncludeInExport: true
      const exportColumns = columns.value.filter(col => col.includeInExport)

      // Bygg kolonnemapping
      const columnMappings = buildColumnMappings(exportColumns, columns.value, dateFormat)

      // Generer filnavn
      const exportDatetime = new Date().toISOString().substring(0, 10).replaceAll(':', '_')
      const itemName = `${query.value.head.systemObjectName} (${query.value.head.itemName}) ${exportDatetime}`
      const fileExtension = exportSettings.exportType === 'XLSX' ? 'xlsx' : exportSettings.format?.toLowerCase() || 'csv'
      const fileName = `${itemName}.${fileExtension}`

      // Bygg filinnstillinger
      const fileReaderOptions = buildFileReaderOptions(exportSettings)

      // Generer navn og beskrivelse
      const importName = `Import ${fileName}`
      const importDescription = `Automatisk opprettet importdefinisjon for tabell ${query.value.head.systemObjectName} fra fil ${fileName} ${new Date().toLocaleDateString('no-NO')}`

      // Kaller backend-endpoint
      const response = await axiosClient.post('/v2/import/create-template-from-export', {
        tableDefinitionId: query.value.head.tableDefinitionId,
        tableName: query.value.head.dbTableName,
        tableSchemaName: query.value.head.tableSchemaName,
        fileName,
        dateFormat,
        fileReaderOptions,
        columns: columnMappings,
        name: importName,
        description: importDescription
      })

      if (response?.data?.importDefinitionId) {
        notificationsStore.addSuccess('Importmal opprettet', `Importmalen "${importName}" er opprettet`)
        return response.data.importDefinitionId
      }
    } catch (error) {
      console.error('Feil ved opprettelse av importmal:', error)
      notificationsStore.addError('Kunne ikke opprette importmal', error.response?.data?.title || error.message || 'En uventet feil oppsto')
      throw error
    }
  }

  async function createExportTemplateFromExport (exportSettings)
  {
    try {
      if (!query.value?.head) {
        notificationsStore.addError('Mangler tabellinformasjon', 'Kunne ikke opprette eksportmal')
        return
      }

      // Generer navn
      const exportDatetime = new Date().toISOString().substring(0, 10)
      const exportName = `Eksport ${query.value.head.systemObjectName} (${query.value.head.itemName}) ${exportDatetime}`

      // Map file type
      const fileType = exportSettings.exportType === 'XLSX' ? 'XLSX' : exportSettings.format || 'CSV'

      // Kaller backend-endpoint for å lagre eksportmal
      const response = await axiosClient.post('save-export-definition', {
        databaseObjectId: query.value.head.databaseObjectId,
        fileType,
        active: true,
        includeHeaders: exportSettings.includeHeaders,
        separator: exportSettings.delimiter || 'Semikolon',
        rowSeparator: '<CR><LF> (Windows)',
        quote: '"',
        encoding: exportSettings.encoding || 'utf-8',
        languageCode: 'nb-NO',
        excelTemplate: ''
      })

      if (response?.data?.exportDefinitionId) {
        notificationsStore.addSuccess('Eksportmal opprettet', `Eksportmalen "${exportName}" er opprettet`)
        return response.data.exportDefinitionId
      }
    } catch (error) {
      console.error('Feil ved opprettelse av eksportmal:', error)
      notificationsStore.addError('Kunne ikke opprette eksportmal', error.response?.data?.title || error.message || 'En uventet feil oppsto')
      throw error
    }
  }

  async function updateExportProgress (progress)
  {
    exportProgress.value = progress;
    console.log('Export progress oppdatert:', progress);

    // Håndter cancellation umiddelbart
    if (progress.status === 'Cancelled') {
      console.log('Export cancelled:', progress)
      downloading.value = false;
      downloadingType.value = null;
      notificationsStore.addWarning('Eksport avbrutt', 'Eksporten ble avbrutt av bruker')

      if (progress.exportId === activeExportTaskId.value) {
        activeExportTaskId.value = null; // Reset for denne fanen
      }

      return;
    }

    // Håndter completed status
    if (progress.status === 'Completed') {
      // Valider at dette er eksporten startet av denne fanen
      if (progress.exportId !== activeExportTaskId.value) {
        console.log('Export completed, but not initiated by this tab. Skipping download.',
          { receivedId: progress.exportId, activeId: activeExportTaskId.value });
        return;
      }

      console.log('Downloading blob:', progress)
      await downloadBlob({ exportId: progress.exportId, contentType: progress.contentType, name: progress.name })
      downloading.value = false;
      downloadingType.value = null;
      activeExportTaskId.value = null; // Reset etter nedlasting
    }

    // Håndter error status
    if (progress.status === 'Error') {
      if (progress.exportId === activeExportTaskId.value) {
        activeExportTaskId.value = null;
        downloading.value = false;
        downloadingType.value = null;
      }
    }

    // Håndter running status - oppdater downloading state
    if (progress.status === 'Running') {
      downloading.value = true;
    }
  }

  async function cancelExport (taskId)
  {
    try {
      // Oppdater UI umiddelbart for bedre brukeropplevelse
      if (exportProgress.value) {
        exportProgress.value.status = 'Cancelling';
        exportProgress.value.message = 'Avbryter eksport...';
      }

      const response = await axiosClient.post(
        '/v2/export/cancel',
        { taskId },
        {
          responseType: 'json',
          validateStatus: status => status < 500
        }
      )

      console.log('Cancel export response:', response.data)
      if (response.status === 200) {
        notificationsStore.addSuccess('Avbryt forespurt', 'Forespørsel om å avbryte eksport er sendt')
      } else {
        notificationsStore.addError('Avbryt feilet', response.data?.message || 'Kunne ikke avbryte eksport')
        // Tilbakestill status hvis avbryt feilet
        if (exportProgress.value) {
          exportProgress.value.status = 'Running';
          exportProgress.value.message = '';
        }
      }
    } catch (error) {
      console.error('Error cancelling export:', error)
      notificationsStore.addError('Avbryt feilet', 'Kunne ikke avbryte eksport')
      // Tilbakestill status hvis avbryt feilet
      if (exportProgress.value) {
        exportProgress.value.status = 'Running';
        exportProgress.value.message = '';
      }
    }
  }

  async function downloadBlob ({ exportId, contentType, name })
  {
    if (!exportId) {
      notificationsStore.addError('Nedlastingsfeil', 'Kunne ikke hente eksporterte filer.');
      return;
    }
    try {
      const response = await axiosClient.get(`/v2/export/${exportId}`, { responseType: 'blob' });
      // Bruk forceFileDownload for nedlastingslogikken (DRY)
      forceFileDownload(response.data, name, contentType);
    } catch (error) {
      console.error('Error downloading file:', error);
      notificationsStore.addError('Nedlastingsfeil', 'Kunne ikke laste ned filen.');
    }
  }

  return {
    loadQuery,
    getQuery,
    showDetails,
    selectedItem,
    getFilter,
    hasFilter,
    hasOrderByAsc,
    hasOrderByDesc,
    addFilter,
    addFilterActionOperator,
    parseQueryOperator,
    setSorting,
    refreshQuery,
    clearAllFilters,
    clearOrderBy,
    addFilterNull,
    addFilterNotNull,
    clearFilter,
    removeFilter,
    queryUpdated,
    whereItems,
    orderByItems,
    pagedResult,
    totalRowsInTable,
    saveData,
    updateRecords,
    softDeleteRows,
    restoreRows,
    addRecord,
    download,
    downloading,
    downloadingType,
    getAuditLog,
    auditLog,
    selectedRows,
    hasSelectedRows,
    isSelected,
    selectSingleRow,
    addRowSelection,
    addRangeSelection,
    toggleRowSelection,
    clearSelection,
    handleBulkSelection,
    totalItems,
    selectedCount,
    getSelectionIcon,
    selectedRowIds,
    columns,
    updateColumnWidth,
    updateColumnOrdinal,
    updateColumnVisibility,
    updateColumnInDetails,
    updateColumnIncludeInExport,
    updateColumnInputFormNew,
    showAllColumns,
    hideAllColumns,
    showAllSystemColumns,
    hideAllSystemColumns,
    setAllReadOnly,
    saveColumnChanges,
    queryRunning,
    saveQuery,
    deleteQuery,
    downloadExport,
    cancelExport,
    exportProgress,
    downloadBlob,
    updateExportProgress,
    activeExportTaskId,
    createImportTemplateFromExport,
    createExportTemplateFromExport
  };
});

